import { useMemo } from 'react';
import useProposals from './useProposals';
import useWorkOrders from './useWorkOrders';
import useInvoices from './useInvoices';
import useProjects from './useProjects';
import useCommissions from './useCommissions'; // item 1169 — Budget+ (static + FY commissions)
import useCurrentUser from './useCurrentUser'; // item 1169 — full ledger is admin-eyes only
import { CommissionStatus } from '../models/commission';
import usePurchaseOrders from './usePurchaseOrders';
import { ProposalStatus } from '../models/proposal';
import { WorkOrderStatus } from '../models/workOrder';
import { InvoiceStatus } from '../models/invoice';
import useCompanyFiscal from './useCompanyFiscal';
import { fiscalYearRange, inRange } from '../models/companyFiscal';
import { selectOpenAr, selectOpenArCharges } from '../models/accountsReceivable';
import useSubscriptionCharges from './useSubscriptionCharges'; // item 1127 — sub AR
import useCreditMemos from './useCreditMemos'; // item 1221 — applied credit counts as billed
import {
  appliedCreditMap, proposalDealAmount, proposalRemainingToBill, proposalBilledMap, proposalInvoiceDealMap,
} from '../models/pipeline';
import { useRecurringRevenues } from './useRecurringRevenues';
import { RecurringRevenueStatus, monthlyRecurringValue } from '../models/recurringRevenue';
import useCompanyDefaults from './useCompanyDefaults';
import { isArchivedRow } from '../utils/archived';

export interface ActivityRow {
  id: string;
  kind: 'proposal' | 'invoice' | 'workOrder';
  when: string;
  label: string;
  sub: string;
  href: string;
}

export interface ActionRow {
  id: string;
  kind: 'proposal' | 'workOrder' | 'invoice';
  label: string;
  sub: string;
  note: string;
  href: string;
}

// item 1481 (Chad: "a break down modal if I click a KPI where it will show me how the numbers
// are derived. This way I can have real numbers") — every tile carries its own derivation:
// the plain-English rule, the rows that made the number, and the arithmetic.
export interface KpiBreakdownRow {
  label: string; // "INV-100547" / "PROP-100677" / "Sub charge"
  sub?: string; // customer · project · status
  amount: number;
  href?: string;
}
export interface KpiBreakdownSection {
  title: string;
  rows: KpiBreakdownRow[];
  total: number;
  note?: string;
}
export interface KpiBreakdown {
  key: string;
  title: string;
  rule: string[]; // the filter/formula in plain English
  sections: KpiBreakdownSection[];
  arithmetic: { label: string; amount: number; op?: '+' | '−' | '=' | '÷' }[]; // the final sum, line by line
  total: number;
}

export interface DashboardMetrics {
  pipelineValue: number;
  // item 1141 — Pending quotes' full deal value, split OUT of the pipeline (Chad: pipeline
  // = sold/PO'ed only; opportunities are their own number)
  pendingOpportunities: number;
  // item 1155 (Chad: "the all things") — TOTAL BOOK = Pipeline (contracted) + Opportunities
  // (potential): everything out there, won or awaiting a decision
  totalBook: number;
  pending: number;
  approved: number;
  deposited: number;
  proposalCount: number;
  collected: number;
  invoiceCount: number;
  paidCount: number;
  paidValue: number; // item 118: PaidEntered invoices $ — confirmed received, FY-scoped
  openWO: number;
  woCount: number;
  projectCount: number;
  outstanding: number;
  outstandingCount: number;
  // item 64 — recurring revenue: monthly recurring revenue, active count, and the
  // current calendar month's churn (cancelled MRR ÷ start-of-period MRR).
  mrr: number;
  arr: number; // item 1132 — mrr × 12, the yearly recurring view
  monthRevenue: number; // item 1132 — THIS month's finalized money (invoices + sub charges)
  activeRecurringCount: number;
  churnedMrr: number;
  churnRate: number;
  // item 468 — past due: open AR invoices whose due date (date + Net-N term) has passed
  pastDueCount: number;
  pastDueTotal: number;
  // item 469 — static-overhead coverage: monthly overhead (annual budget ÷ 12), how much
  // MRR still needs to cover it, and the coverage fraction (mrr / monthlyOverhead)
  annualStaticBudget: number;
  monthlyOverhead: number;
  fyCommissions: number; // item 1169 — commissions accrued this FY (Budget+ share)
  budgetWithCommissions: number; // item 1169 — static budget + fyCommissions
  overheadRemaining: number;
  overheadCoverage: number;
  topCustomers: { name: string; total: number }[];
  lastMonthRevenue: number; // item 1481 — last calendar month's finalized money (the break-even you remember)
  lastMonthDelta: number;
  breakdowns: Record<string, KpiBreakdown>; // item 1481 — keyed by KPI tile key
  activity: ActivityRow[];
  // "what you're working on" — the items that need a decision/action (item 96)
  actions: ActionRow[];
  isLoading: boolean;
}

// The dashboard's single source of numbers (item 96). Extracted from DashboardPage
// so the always-visible KPI strip, the expanded bento, and the landing active-work
// region all read the SAME computed metrics (and share react-query's cache).
const useDashboardMetrics = (): DashboardMetrics => {
  const { data: proposals, isLoading: lp } = useProposals();
  const { data: workOrders, isLoading: lw } = useWorkOrders();
  const { data: invoices, isLoading: li } = useInvoices();
  const { data: projects, isLoading: lj } = useProjects();
  const { data: purchaseOrders, isLoading: lo } = usePurchaseOrders(); // item 1069
  const { data: recurring, isLoading: lr } = useRecurringRevenues();
  const { data: commissionData } = useCommissions(); // item 1169
  const { isAdmin, isSuperAdmin } = useCurrentUser(); // item 1169
  const { charges } = useSubscriptionCharges(); // item 1127 — unpaid sub charges are open AR
  const { applications: creditApplications } = useCreditMemos(); // item 1221 — billed add-back
  const { fiscal } = useCompanyFiscal();
  const { defaults } = useCompanyDefaults(); // item 468/469 — Net-N term + annual static budget

  return useMemo<DashboardMetrics>(() => {
    const proposalRows = proposals?.index ?? [];
    const wos = workOrders?.index ?? [];
    const invs = invoices?.index ?? [];
    const projs = projects?.index ?? [];
    const pos = purchaseOrders?.index ?? [];

    // item 868b (Chad) — an UNSENT customer request has no numbers yet, so it isn't pipeline:
    // it stays out of every KPI until we price-and-send (at which point it's a normal Pending).
    const isUnsentRequest = (p: any) => !!p.isCustomerSubmission && !p.sentDate;
    // item 1068 — the status buckets count LIVE rows only: a voided/archived proposal must
    // drop out of every KPI the moment it's flagged (archiving test data proved they didn't)
    // item 1180 (Chad) — PAPER ONLY proposals (legacy contract sources) clear from every
    // KPI exactly like void/archive, while staying live for the contract engine.
    const isLiveProposal = (p: any) => !p.isVoid && !isArchivedRow(p) && !p.isContractOnly;
    const pending = proposalRows.filter(
      (p) => p.status === ProposalStatus.Pending && isLiveProposal(p) && !isUnsentRequest(p),
    );
    const approved = proposalRows.filter((p) => p.status === ProposalStatus.Approved && isLiveProposal(p));
    const deposited = proposalRows.filter((p) => p.status === ProposalStatus.ApprovedDeposited && isLiveProposal(p));
    // item 829 (Chad) — a DECLINED proposal stays in the list (like a void) but is DEAD pipeline:
    // it drops out of the Proposals KPI count, exactly like void/archived already do nothing for
    // pipeline value. "Live" proposals = anything not declined, voided, or archived.
    const livePipelineProposals = proposalRows.filter(
      (p) => p.status !== ProposalStatus.Declined && !(p as any).isVoid && !isArchivedRow(p)
        && !isUnsentRequest(p),
    );
    // item 1068/1069 (Chad) — pipeline = ALL money on its way in, every stage (replaces item
    // 119's narrow Entered-only slice, which read ~nothing):
    //   1) open AR — billed and unpaid (selectOpenAr: paid-awaiting-deposit already left)
    //   2) processed-not-billed — remaining-to-bill on won proposals (Approved full,
    //      ApprovedDeposited final 50%; the deposit invoice sits in AR, no double-count)
    //   3) proposed — Pending quotes at full deal amount (awaiting the customer's decision)
    //   4) open NON-proposal WOs/SOs at retail (standalone service + PO conversions) —
    //      proposal WOs are covered by (2); an invoiced non-proposal WO locks and drops out
    //   5) live POs not yet converted to a WO/SO, at cost (the only number they have yet);
    //      once converted the WO/SO in (4) takes over
    // item 1127 — open AR = unpaid one-time invoices + unpaid subscription charges (same
    // money owed; both leave AR when the payment arrives). Everything downstream of openAr
    // (Outstanding, its count, past-due, the pipeline's AR slice) counts both.
    const openAr = [...selectOpenAr(invs), ...selectOpenArCharges(charges)] as typeof invs;
    const outstanding = openAr.reduce((s, i) => s + (i.retailTotal ?? 0), 0);
    // item 1137 — ACTUAL billed per proposal (finalized live invoices): the pipeline
    // remainder is deal − billed, not a stage guess
    // item 1221 — applied customer credit counts as billed (money the deal already collected)
    const billedMap = proposalBilledMap(invs as any, appliedCreditMap(creditApplications));
    // item 1146 — invoice-derived deal totals (full line copies) — Bally's-class truth
    const dealMap = proposalInvoiceDealMap(invs as any);
    // item 1141 (Chad: "pipeline = sold/PO'ed projects, not potential opportunities") —
    // Pending quotes leave the pipeline and become their own number

    // item 1141 — draft invoices from NON-proposal WOs: the WO locked at generate (drops out
    // of the WO slice) but the draft isn't finalized (not in AR) — this is that gap. Proposal
    // drafts are deliberately NOT added: they're already inside the won-unbilled remainder.
    const draftNonProposalInvoices = invs
      .filter((i: any) => !i.isVoid && !i.isFinalized && !i.proposalId
        && i.status !== InvoiceStatus.WrittenOff)
      .reduce((s, i: any) => s + (i.retailTotal ?? 0), 0);
    // item 1154 (Chad) — an UNCONVERTED SALES ORDER is a quote, not contracted work:
    // it belongs in Opportunities. Only true service WOs stay in the pipeline.
    const openNonProposalWos = wos.filter((w: any) => !w.isVoid && !isArchivedRow(w)
      && !w.proposalId && w.status !== WorkOrderStatus.InvoicesGenerated);
    const pipelineWos = openNonProposalWos.filter((w: any) => !w.isSalesOrder);
    const openSalesOrders = openNonProposalWos.filter((w: any) => w.isSalesOrder);
    // item 1141/1154 — Opportunities = pending quotes + unconverted sales orders
    const pendingOpportunities = pending.reduce((s, p) => s + proposalDealAmount(p), 0)
      + openSalesOrders.reduce((s, w: any) => s + (w.retailTotal ?? 0), 0);
    const convertedPoIds = new Set(
      wos.filter((w: any) => !w.isVoid && !isArchivedRow(w) && w.sourcePurchaseOrderId)
        .map((w: any) => w.sourcePurchaseOrderId as string),
    );
    // item 1184 (Chad's PO questions) — ARCHIVED POs must leave the pipeline like voided
    // ones (the 1068 proposal lesson, never applied here: an archived PO counted at cost
    // forever).
    const pipelinePos = pos.filter((po: any) => !po.isVoid && !isArchivedRow(po) && !convertedPoIds.has(po.id));
    // item 1141 — PIPELINE = CONTRACTED money not yet in the bank: open AR (incl. sub
    // charges) + won-unbilled (ACTUAL, item 1137) + open non-proposal WOs + committed POs +
    // non-proposal draft invoices. Pending quotes live in pendingOpportunities instead.
    const pipelineValue = outstanding
      + [...approved, ...deposited].reduce((s, p) => s + proposalRemainingToBill(p, billedMap, dealMap), 0)
      + pipelineWos.reduce((s, w: any) => s + (w.retailTotal ?? 0), 0)
      + pipelinePos.reduce((s, po: any) => s + (po.costTotal ?? 0), 0)
      + draftNonProposalInvoices;

    const liveInvoices = invs.filter((i) => !i.isVoid);
    // Fiscal-year window (item 68): "Invoiced (FY)" revenue + top customers scope to
    // the CURRENT fiscal year by invoice date and RESET at rollover. Outstanding AR
    // below is deliberately NOT windowed — carry-over #1 (open AR stays until paid).
    const fyRange = fiscalYearRange(fiscal?.yearStartMonth);
    // item 1073 (Chad: "why does invoiced say 2k") — DRAFTS are not invoiced revenue: an
    // unfinalized invoice is still editable and unsent, so it stays out of Invoiced (FY),
    // Paid, and Top customers until Finalize locks it.
    const fyInvoices = liveInvoices.filter(
      (i) => (i as any).isFinalized && inRange((i as any).date, fyRange),
    );
    // item 485 — a written-off invoice is not revenue: keep it out of Invoiced (FY)
    const collected = fyInvoices
      .filter((i) => i.status !== InvoiceStatus.WrittenOff)
      .reduce((s, i) => s + (i.retailTotal ?? 0), 0);
    const paidCount = fyInvoices.filter(
      (i) => i.status === InvoiceStatus.Paid || i.status === InvoiceStatus.PaidEntered,
    ).length;
    // item 118: Paid KPI — PaidEntered (confirmed received) only, FY-scoped
    const paidValue = fyInvoices
      .filter((i) => i.status === InvoiceStatus.PaidEntered)
      .reduce((s, i) => s + (i.retailTotal ?? 0), 0);

    const liveWos = wos.filter((w) => !(w as any).isVoid && !isArchivedRow(w)); // item 714
    const openWO = liveWos.filter((w) => w.status !== WorkOrderStatus.InvoicesGenerated).length;

    // outstanding AR (items 44/67) — carry-over #1 (item 68): openAr above is ALL open
    // (non-void, not-paid) invoices, NOT fiscal-year-scoped, so receivables survive the
    // FY rollover until paid. Shares selectOpenAr with the AR report so they reconcile.

    // item 64 — MRR (sum of active schedules normalised to monthly) + this calendar
    // month's churn (schedules cancelled this month, by their normalised MRR).
    // item 1166 (Chad: "lets not show it") — ADMINISTRATIVE projects' subs (the office
    // sublease) stay OUT of the KPI money entirely: MRR/ARR/churn/month-revenue. The
    // accountant ledgers still carry their charges — this only purifies the metrics.
    const adminProjIds = new Set(projs.filter((p: any) => p.isAdministrative).map((p: any) => p.id));
    const recurringRows = (recurring?.index ?? [])
      .filter((r) => !r.isVoid && !adminProjIds.has(r.projectId));
    const adminRecurringIds = new Set((recurring?.index ?? [])
      .filter((r) => adminProjIds.has(r.projectId)).map((r) => r.id));
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const activeRecurring = recurringRows.filter((r) => r.status === RecurringRevenueStatus.Active);
    const mrr = activeRecurring.reduce((s, r) => s + monthlyRecurringValue(r), 0);
    // item 1132 (Chad) — the yearly view of the same number
    const arr = mrr * 12;
    // item 1132 — THIS calendar month's finalized money (one-time invoices + subscription
    // charges issued this month) vs the monthly overhead — the break-even pulse
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    // item 1482 (Chad 2026-09-27: "include imported") — the QuickBooks backfill imports now count
    // in month revenue / break-even exactly as they do in Invoiced (FY) and Top customers: one
    // rule everywhere. (Supersedes item 1227/1228's exclusion.)
    const monthRevenue = invs
      .filter((i: any) => i.isFinalized && !i.isVoid && i.status !== InvoiceStatus.WrittenOff
        && String(i.date ?? '').slice(0, 7) === ym)
      .reduce((s, i: any) => s + (i.retailTotal ?? 0), 0)
      + charges
        .filter((c) => String(c.issuedDate ?? '').slice(0, 7) === ym
          && !adminRecurringIds.has(c.recurringRevenueId))
        .reduce((s, c) => s + (c.amount ?? 0), 0);
    // item 1481 — LAST month's finalized money the same way, so the break-even tile can say
    // "last month you did +X" next to this month's running number
    const lastM = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const ymLast = `${lastM.getFullYear()}-${String(lastM.getMonth() + 1).padStart(2, '0')}`;
    const monthInvoiceRows = (key: string) => invs
      .filter((i: any) => i.isFinalized && !i.isVoid && i.status !== InvoiceStatus.WrittenOff
        && String(i.date ?? '').slice(0, 7) === key); // item 1482 — imports included
    const monthChargeRows = (key: string) => charges
      .filter((c) => String(c.issuedDate ?? '').slice(0, 7) === key && !adminRecurringIds.has(c.recurringRevenueId));
    const lastMonthRevenue = monthInvoiceRows(ymLast).reduce((s, i: any) => s + (i.retailTotal ?? 0), 0)
      + monthChargeRows(ymLast).reduce((s, c) => s + (c.amount ?? 0), 0);
    const churnedMrr = recurringRows
      .filter((r) => r.status === RecurringRevenueStatus.Cancelled
        && r.lastModifiedDate && new Date(r.lastModifiedDate) >= monthStart)
      .reduce((s, r) => s + monthlyRecurringValue(r), 0);
    const churnRate = (mrr + churnedMrr) > 0 ? churnedMrr / (mrr + churnedMrr) : 0;

    // item 468 — past due: open AR invoices whose due date (invoice date + the company's
    // Net-N payment term) has already passed. openAr is the non-void, unpaid, finalized set.
    const termDays = defaults?.invoicePaymentTermDays ?? 30;
    const nowMs = Date.now();
    const pastDueInvs = openAr.filter((i) => {
      const d = (i as any).date;
      if (!d) return false;
      const dueMs = new Date(d).getTime() + termDays * 86400000;
      return dueMs < nowMs;
    });
    const pastDueCount = pastDueInvs.length;
    const pastDueTotal = pastDueInvs.reduce((s, i) => s + (i.retailTotal ?? 0), 0);

    // item 469 — static-overhead coverage from MRR
    const annualStaticBudget = defaults?.annualStaticBudget ?? 0;
    // item 1169 (Chad) — BUDGET+ : every accrued commission ADDS to the static budget for
    // the fiscal year it accrues in (created date), rather than charging any single month —
    // jobs finish and pay out on their own schedule, so the cost averages across the year.
    // Reversed/Cancelled rows drop back out automatically (1167). FY rollover resets the
    // bucket by date; post-December accruals simply belong to the next FY. Admin eyes only —
    // non-admins see just their own rows, which would understate the number.
    const fyCommissions = (isAdmin || isSuperAdmin)
      ? (commissionData?.index ?? [])
        .filter((c) => c.status !== CommissionStatus.Cancelled
          && c.status !== CommissionStatus.Reversed
          && inRange(c.createdDate, fyRange))
        .reduce((s2, c) => s2 + (c.amount ?? 0), 0)
      : 0;
    const budgetWithCommissions = annualStaticBudget + fyCommissions;
    const monthlyOverhead = budgetWithCommissions / 12;
    const overheadRemaining = Math.max(0, monthlyOverhead - mrr);
    const overheadCoverage = monthlyOverhead > 0 ? mrr / monthlyOverhead : 1;

    // top customers by invoiced revenue (item 65) — fiscal-year-scoped (item 68)
    const byCustomer = new Map<string, number>();
    fyInvoices.forEach((i) => {
      const name = (i as any).customerName ?? 'Unnamed';
      byCustomer.set(name, (byCustomer.get(name) ?? 0) + (i.retailTotal ?? 0));
    });
    const topCustomers = Array.from(byCustomer.entries())
      .map(([name, total]) => ({ name, total }))
      .filter((c) => c.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);

    // recent activity (item 66): union of recent documents by lastModifiedDate
    const activity: ActivityRow[] = [
      ...proposalRows.map((p) => ({
        id: `p-${p.id}`,
        kind: 'proposal' as const,
        when: p.lastModifiedDate,
        label: `Proposal ${p.proposalNumber}`,
        sub: p.customerName ?? '',
        href: `/proposal/${p.id}`,
      })),
      ...invs.map((i) => ({
        id: `i-${i.id}`,
        kind: 'invoice' as const,
        when: i.lastModifiedDate,
        label: `Invoice ${i.invoiceNumber}`,
        sub: (i as any).customerName ?? '',
        href: `/invoice/${i.id}`,
      })),
      ...wos.map((w) => ({
        id: `w-${w.id}`,
        kind: 'workOrder' as const,
        when: (w as any).lastModifiedDate,
        label: `Work order ${w.workOrderNumber}`,
        sub: (w as any).customerName ?? '',
        href: `/WorkOrder/${w.id}`,
      })),
    ]
      .filter((a) => a.when)
      .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
      .slice(0, 6);

    // "what you're working on" (item 96) — concrete next actions, most urgent first:
    //  1) proposals awaiting a decision (Pending)
    //  2) approved/deposited proposals with money still to bill (open WO from them)
    //  3) open work orders not yet fully invoiced
    const actions: ActionRow[] = [
      ...pending.map((p) => ({
        id: `ap-${p.id}`,
        kind: 'proposal' as const,
        label: `Proposal ${p.proposalNumber}`,
        sub: p.customerName ?? '',
        note: 'Awaiting decision',
        href: `/proposal/${p.id}`,
      })),
      ...deposited.map((p) => ({
        id: `ad-${p.id}`,
        kind: 'proposal' as const,
        label: `Proposal ${p.proposalNumber}`,
        sub: p.customerName ?? '',
        note: 'Balance left to bill',
        href: `/proposal/${p.id}`,
      })),
      ...liveWos
        .filter((w) => w.status !== WorkOrderStatus.InvoicesGenerated)
        .map((w) => ({
          id: `aw-${w.id}`,
          kind: 'workOrder' as const,
          label: `Work order ${w.workOrderNumber}`,
          sub: (w as any).customerName ?? '',
          note: 'Open — ready to invoice',
          href: `/WorkOrder/${w.id}`,
        })),
      // item 152 — finalized invoices waiting for the accountant to enter into the
      // books (New = finalized & sent, item 116). Labelled by invoice number so the
      // accountant can reconcile against QuickBooks; mirrors the Accountant page list.
      ...invs
        .filter((i) => !i.isVoid && (i as any).isFinalized && i.status === InvoiceStatus.New)
        .map((i) => ({
          id: `ai-${i.id}`,
          kind: 'invoice' as const,
          label: `Invoice ${i.invoiceNumber}`,
          sub: (i as any).customerName ?? '',
          note: 'Awaiting entry',
          href: '/accountant',
        })),
    ].slice(0, 12);

    // ---- item 1481 — the derivations behind every tile ---------------------------------
    const invRow = (i: any, note?: string): KpiBreakdownRow => ({
      label: i.invoiceNumber ?? 'Invoice',
      sub: [i.customerName, i.projectName, note ?? (i.date ? String(i.date).slice(0, 10) : null)].filter(Boolean).join(' · '),
      amount: i.retailTotal ?? 0,
      href: `/invoice/${i.id}`,
    });
    const chargeRow = (c: any): KpiBreakdownRow => ({
      label: c.chargeNumber ?? c.stripeInvoiceNumber ?? 'Subscription charge',
      sub: [c.customerName, c.projectName, c.issuedDate ? String(c.issuedDate).slice(0, 10) : null].filter(Boolean).join(' · '),
      amount: c.amount ?? 0,
    });
    const propRow = (p: any, amount: number, note: string): KpiBreakdownRow => ({
      label: p.proposalNumber ?? 'Proposal', sub: [p.customerName, p.proposalTitle, note].filter(Boolean).join(' · '), amount, href: `/proposal/${p.id}`,
    });
    const woRow = (w: any, amount: number, note: string): KpiBreakdownRow => ({
      label: w.workOrderNumber ?? 'Work order', sub: [w.customerName, w.workOrderTitle, note].filter(Boolean).join(' · '), amount, href: `/WorkOrder/${w.id}`,
    });
    const sum = (rows: KpiBreakdownRow[]) => rows.reduce((s, r) => s + r.amount, 0);
    const section = (title: string, rows: KpiBreakdownRow[], note?: string): KpiBreakdownSection => ({
      title, rows: rows.slice().sort((a, b) => b.amount - a.amount), total: sum(rows), note,
    });
    const fyLabel = fyRange ? `${String(fyRange.from).slice(0, 10)} → ${String(fyRange.to).slice(0, 10)}` : 'this fiscal year';

    const openArInvoiceRows = selectOpenAr(invs).map((i: any) => invRow(i, `${InvoiceStatus[i.status] ?? ''}`));
    const openArChargeRows = selectOpenArCharges(charges).map((c: any) => chargeRow(c));
    const wonUnbilledRows = [...approved, ...deposited]
      .map((p) => propRow(p, proposalRemainingToBill(p, billedMap, dealMap), `${p.status === ProposalStatus.Approved ? 'Approved' : 'In progress'} · remaining to bill`))
      .filter((r) => r.amount > 0);
    const pipelineWoRows = pipelineWos.map((w: any) => woRow(w, w.retailTotal ?? 0, 'open standalone WO at retail'));
    const pipelinePoRows = pipelinePos.map((po: any) => ({
      label: po.purchaseOrderNumber ?? 'PO', sub: [po.vendorName, po.projectName, 'unconverted PO at cost'].filter(Boolean).join(' · '), amount: po.costTotal ?? 0, href: `/PurchaseOrder/${po.id}`,
    }));
    const draftNonPropRows = invs
      .filter((i: any) => !i.isVoid && !i.isFinalized && !i.proposalId && i.status !== InvoiceStatus.WrittenOff)
      .map((i: any) => invRow(i, 'draft, non-proposal'));
    const pendingRows = pending.map((p) => propRow(p, proposalDealAmount(p), 'Pending quote at full deal value'));
    const salesOrderRows = openSalesOrders.map((w: any) => woRow(w, w.retailTotal ?? 0, 'unconverted sales order'));
    const fyInvoiceRows = fyInvoices.filter((i) => i.status !== InvoiceStatus.WrittenOff).map((i: any) => invRow(i));
    const paidRows = fyInvoices.filter((i) => i.status === InvoiceStatus.PaidEntered).map((i: any) => invRow(i, 'Paid · entered'));
    const thisMonthInvRows = monthInvoiceRows(ym).map((i: any) => invRow(i));
    const thisMonthChargeRows = monthChargeRows(ym).map((c: any) => chargeRow(c));
    const lastMonthInvRows = monthInvoiceRows(ymLast).map((i: any) => invRow(i));
    const lastMonthChargeRows = monthChargeRows(ymLast).map((c: any) => chargeRow(c));
    const fyCommissionRows = (isAdmin || isSuperAdmin)
      ? (commissionData?.index ?? [])
        .filter((c) => c.status !== CommissionStatus.Cancelled && c.status !== CommissionStatus.Reversed && inRange(c.createdDate, fyRange))
        .map((c) => ({ label: c.earnedByName ?? 'Earner', sub: [c.description || c.reasonName, c.customerName, c.createdDate ? String(c.createdDate).slice(0, 10) : null].filter(Boolean).join(' · '), amount: c.amount ?? 0 }))
      : [];
    const monthlyOverheadLabel = 'Budget+ ÷ 12';
    const breakdowns: Record<string, KpiBreakdown> = {
      pipeline: {
        key: 'pipeline',
        title: 'Pipeline',
        total: pipelineValue,
        rule: [
          'Contracted money not yet in the bank. Pending quotes are NOT here (see Opportunities).',
          'Open AR = finalized, unpaid one-time invoices + unpaid subscription charges.',
          "Won, unbilled = each Approved / In-progress proposal's deal value minus what its finalized invoices (and applied credits) already billed.",
          'Open standalone work orders at retail; unconverted purchase orders at cost; non-proposal draft invoices.',
          'Void, archived, declined and paper-only documents never count.',
        ],
        sections: [
          section('Open AR — invoices', openArInvoiceRows), section('Open AR — subscription charges', openArChargeRows),
          section('Won, not yet billed', wonUnbilledRows), section('Open standalone work orders', pipelineWoRows),
          section('Committed purchase orders (cost)', pipelinePoRows), section('Draft invoices (non-proposal)', draftNonPropRows),
        ],
        arithmetic: [
          { label: 'Open AR', amount: outstanding, op: '+' },
          { label: 'Won, not yet billed', amount: sum(wonUnbilledRows), op: '+' },
          { label: 'Open standalone WOs', amount: sum(pipelineWoRows), op: '+' },
          { label: 'Committed POs', amount: sum(pipelinePoRows), op: '+' },
          { label: 'Draft non-proposal invoices', amount: draftNonProposalInvoices, op: '+' },
          { label: 'Pipeline', amount: pipelineValue, op: '=' },
        ],
      },
      opportunities: {
        key: 'opportunities',
        title: 'Opportunities',
        total: pendingOpportunities,
        rule: ['Potential work awaiting a decision: sent Pending quotes at full deal value + unconverted sales orders at retail.', 'Unsent customer requests have no numbers yet and stay out.'],
        sections: [section('Pending quotes', pendingRows), section('Unconverted sales orders', salesOrderRows)],
        arithmetic: [{ label: 'Pending quotes', amount: sum(pendingRows), op: '+' }, { label: 'Sales orders', amount: sum(salesOrderRows), op: '+' }, { label: 'Opportunities', amount: pendingOpportunities, op: '=' }],
      },
      totalbook: {
        key: 'totalbook',
        title: 'Total Book',
        total: pipelineValue + pendingOpportunities,
        rule: ['Everything out there: Pipeline (contracted) + Opportunities (potential).'],
        sections: [],
        arithmetic: [{ label: 'Pipeline', amount: pipelineValue, op: '+' }, { label: 'Opportunities', amount: pendingOpportunities, op: '+' }, { label: 'Total Book', amount: pipelineValue + pendingOpportunities, op: '=' }],
      },
      invoiced: {
        key: 'invoiced',
        title: 'Invoiced (FY)',
        total: collected,
        rule: [`Finalized, non-void invoices whose invoice DATE falls in the fiscal year (${fyLabel}).`, 'Written-off invoices are excluded. Drafts are excluded until Finalize. Paid or not does not matter here.', 'Includes the QuickBooks backfill imports (dated inside the FY).'],
        sections: [section('Invoices', fyInvoiceRows)],
        arithmetic: [{ label: `${fyInvoiceRows.length} invoices`, amount: collected, op: '=' }],
      },
      ar: {
        key: 'ar',
        title: 'Outstanding AR',
        total: outstanding,
        rule: ['Finalized, unpaid one-time invoices + unpaid subscription charges. Not fiscal-year windowed — receivables carry until paid.', 'Paid-awaiting-deposit has already left. Written-off, void and archived never count. Same selection as the AR report.'],
        sections: [section('Invoices', openArInvoiceRows), section('Subscription charges', openArChargeRows)],
        arithmetic: [{ label: 'Invoices', amount: sum(openArInvoiceRows), op: '+' }, { label: 'Subscription charges', amount: sum(openArChargeRows), op: '+' }, { label: 'Outstanding AR', amount: outstanding, op: '=' }],
      },
      paid: {
        key: 'paid',
        title: 'Paid (FY)',
        total: paidValue,
        rule: [`Fiscal-year invoices (${fyLabel}) whose status is Paid · Entered — confirmed received AND entered into the books.`, 'Paid-but-not-entered is not counted yet.'],
        sections: [section('Invoices', paidRows)],
        arithmetic: [{ label: `${paidRows.length} invoices`, amount: paidValue, op: '=' }],
      },
      open: {
        key: 'open',
        title: 'Open work',
        total: openWO + pending.length,
        rule: ['A count, not money: live work orders not yet fully invoiced + Pending quotes awaiting a decision.'],
        sections: [
          section('Open work orders', liveWos.filter((w) => w.status !== WorkOrderStatus.InvoicesGenerated).map((w: any) => woRow(w, w.retailTotal ?? 0, WorkOrderStatus[w.status] ?? ''))),
          section('Pending quotes', pendingRows),
        ],
        arithmetic: [{ label: 'Open work orders', amount: openWO, op: '+' }, { label: 'Pending quotes', amount: pending.length, op: '+' }, { label: 'Open work', amount: openWO + pending.length, op: '=' }],
      },
      overhead: {
        key: 'overhead',
        title: 'Overhead',
        total: overheadRemaining,
        rule: ['Budget+ = the static annual budget (Settings) + every commission accrued this fiscal year (reversed/cancelled rows drop back out).', `Monthly overhead = ${monthlyOverheadLabel}. "Covered" when MRR alone pays it; otherwise the slice MRR does not cover.`],
        sections: [section('Commissions accrued this FY', fyCommissionRows, 'Admin eyes only — these ADD to the static budget for the year.')],
        arithmetic: [
          { label: 'Static annual budget', amount: annualStaticBudget, op: '+' }, { label: 'FY commissions', amount: fyCommissions, op: '+' },
          { label: 'Budget+', amount: budgetWithCommissions, op: '=' }, { label: 'Monthly overhead (÷ 12)', amount: monthlyOverhead, op: '÷' },
          { label: 'MRR (active subscriptions, monthly)', amount: mrr, op: '−' }, { label: 'Overhead not covered by MRR', amount: overheadRemaining, op: '=' },
        ],
      },
      breakeven: {
        key: 'breakeven',
        title: 'Break-even (this month)',
        total: monthRevenue - monthlyOverhead,
        rule: [
          `THIS calendar month (${ym}) only, running: finalized one-time invoices dated this month + subscription charges issued this month, minus the month's Budget+ share.`,
          'Administrative-project subscriptions are excluded. Drafts are excluded until Finalize. QuickBooks-backfill imports count, same as every other KPI (Chad, 2026-09-27).',
          `Last month (${ymLast}) closed at ${lastMonthRevenue >= monthlyOverhead ? '+' : '−'}$${Math.abs(lastMonthRevenue - monthlyOverhead).toLocaleString(undefined, { maximumFractionDigits: 0 })} against the same overhead share.`,
        ],
        sections: [
          section(`This month — invoices (${ym})`, thisMonthInvRows), section(`This month — subscription charges (${ym})`, thisMonthChargeRows),
          section(`Last month — invoices (${ymLast})`, lastMonthInvRows), section(`Last month — subscription charges (${ymLast})`, lastMonthChargeRows),
        ],
        arithmetic: [
          { label: 'This month invoices', amount: sum(thisMonthInvRows), op: '+' }, { label: 'This month subscription charges', amount: sum(thisMonthChargeRows), op: '+' },
          { label: 'This month revenue', amount: monthRevenue, op: '=' }, { label: 'Monthly overhead (Budget+ ÷ 12)', amount: monthlyOverhead, op: '−' },
          { label: 'Break-even, this month', amount: monthRevenue - monthlyOverhead, op: '=' },
          { label: `Last month (${ymLast}) revenue`, amount: lastMonthRevenue, op: '+' }, { label: 'Monthly overhead', amount: monthlyOverhead, op: '−' },
          { label: 'Break-even, last month', amount: lastMonthRevenue - monthlyOverhead, op: '=' },
        ],
      },
    };

    return {
      pipelineValue,
      pendingOpportunities, // item 1141 — Pending quotes, OUTSIDE the pipeline
      totalBook: pipelineValue + pendingOpportunities, // item 1155 — everything out there
      pending: pending.length,
      approved: approved.length,
      deposited: deposited.length,
      proposalCount: livePipelineProposals.length,
      collected,
      invoiceCount: fyInvoices.length,
      paidCount,
      paidValue,
      openWO,
      woCount: liveWos.length,
      projectCount: projs.length,
      outstanding,
      outstandingCount: openAr.length,
      mrr,
      arr, // item 1132
      monthRevenue, // item 1132 — this month's finalized money (invoices + sub charges)
      activeRecurringCount: activeRecurring.length,
      churnedMrr,
      churnRate,
      pastDueCount,
      pastDueTotal,
      annualStaticBudget,
      fyCommissions, // item 1169
      budgetWithCommissions, // item 1169
      monthlyOverhead,
      overheadRemaining,
      overheadCoverage,
      topCustomers,
      lastMonthRevenue, // item 1481
      lastMonthDelta: lastMonthRevenue - monthlyOverhead,
      breakdowns, // item 1481
      activity,
      actions,
      isLoading: lp || lw || li || lj || lr || lo,
    };
  }, [proposals, workOrders, invoices, projects, purchaseOrders, recurring, charges, fiscal, defaults, commissionData, isAdmin, isSuperAdmin,
    creditApplications, lp, lw, li, lj, lr, lo]);
};

export default useDashboardMetrics;
